import { useState, useEffect } from 'react'
import Button from '@cloudscape-design/components/button'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import Tabs from '@cloudscape-design/components/tabs'
import { useProfiles } from '@/hooks/useProfiles'
import { useAppStore } from '@/store/app-store'
import { fetchPresets } from '@/engine/api-client'
import { ProfileCard } from './ProfileCard'
import { ProfileEditor } from './ProfileEditor'
import { VendorFilterPanel } from './VendorFilterPanel'
import type { Profile, PresetProfile } from '@/types'
import {
  colorBackgroundContainerContent,
  colorBorderDividerDefault,
  colorTextBodySecondary,
} from '@cloudscape-design/design-tokens'

export function ProfileList() {
  const { profiles, updateProfile, addProfile, deleteProfile, resetDefaults } = useProfiles()
  const [editing, setEditing] = useState<Profile | null>(null)
  const [adding, setAdding] = useState(false)
  const [activeTab, setActiveTab] = useState('presets')

  const presets = useAppStore((s) => s.presets)
  const presetsLoaded = useAppStore((s) => s.presetsLoaded)
  const setPresets = useAppStore((s) => s.setPresets)
  const executionMode = useAppStore((s) => s.executionMode)
  const isCloud = executionMode === 'cloud'

  useEffect(() => {
    if (presetsLoaded) return
    fetchPresets().then((result) => {
      // Restore saved enabled/prompt/maxTokens state from localStorage
      try {
        const raw = localStorage.getItem('bedrock-diag-preset-state')
        if (raw) {
          const saved = JSON.parse(raw) as Record<string, { enabled: boolean; prompt?: string; maxTokens?: number }>
          const merged = result.map(p => {
            const s = saved[p.id]
            if (s) return { ...p, enabled: s.enabled, ...(s.prompt ? { prompt: s.prompt } : {}), ...(s.maxTokens ? { maxTokens: s.maxTokens } : {}) }
            return p
          })
          setPresets(merged)
          return
        }
      } catch { /* noop */ }
      setPresets(result)
    })
  }, [presetsLoaded, setPresets])

  const handlePresetUpdate = (updated: PresetProfile) => {
    setPresets(presets.map((p) => (p.id === updated.id ? updated : p)))
  }

  const handlePresetToggle = (id: string) => {
    setPresets(presets.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p)))
  }

  const handlePresetResetAll = async () => {
    localStorage.removeItem('bedrock-diag-preset-state')
    const fresh = await fetchPresets()
    setPresets(fresh)
  }

  const handleCustomResetAll = () => {
    resetDefaults()
  }

  const enabledPresetCount = presets.filter((p) => p.enabled).length
  const enabledCustomCount = profiles.filter((p) => p.enabled).length

  return (
    <SpaceBetween size="l">
      <SpaceBetween size="xs">
        <Box variant="h2">Test Profiles</Box>
        <Box variant="p" color="text-body-secondary">
          Profiles define what you're testing - each one specifies a model, region, and configuration.
          Enable the profiles you want to compare and disable the rest.
        </Box>

      </SpaceBetween>

      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId)}
        tabs={[
          {
            id: 'presets',
            label: `Preset Profiles${enabledPresetCount > 0 ? ` (${enabledPresetCount})` : ''}`,
            content: (
              <SpaceBetween size="m">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="normal" onClick={handlePresetResetAll}>Reset All Presets</Button>
                </SpaceBetween>
                <div style={styles.howto}>
                  <Box fontWeight="bold" color="text-status-info">How to use preset profiles</Box>
                  <ol style={styles.howtoList}>
                    <li>Preset profiles use model IDs in your own AWS account (Local mode) or the serverless backend (Cloud mode)</li>
                    <li>By default, system inference profiles support Cross-Region Inference (CRIS)</li>
                    <li>Use the vendor toggles to filter by provider - Amazon Bedrock, Anthropic, or Grok (xAI)</li>
                    <li>Choose a prompt complexity to set the prompt and max tokens automatically, or select "Custom" for your own</li>
                    <li>Toggle "Enabled" on the presets you want to include in the benchmark</li>
                    <li>Preset and custom profiles can run together in the same benchmark</li>
                  </ol>
                </div>
                <VendorFilterPanel
                  presets={presets}
                  onPresetUpdate={handlePresetUpdate}
                  onPresetToggle={handlePresetToggle}
                />
              </SpaceBetween>
            ),
          },
          {
            id: 'custom',
            label: `Custom Profiles${enabledCustomCount > 0 ? ` (${enabledCustomCount})` : ''}`,
            content: (
              <SpaceBetween size="m">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="primary" onClick={() => setAdding(true)}>+ Add Profile</Button>
                  <Button variant="normal" onClick={handleCustomResetAll}>Reset All Custom</Button>
                </SpaceBetween>
                <div style={styles.howto}>
                  <Box fontWeight="bold" color="text-status-info">How to set up custom profiles</Box>
                  <ol style={styles.howtoList}>
                    <li>Click "+ Add Profile" to create a new profile, or edit an existing one</li>
                    {isCloud ? (
                      <>
                        <li>Select the profile type - Bedrock (InvokeModelWithResponseStream, ConverseStream), Direct Anthropic API, or Grok (xAI)</li>
                        <li>Enter a Model ID (e.g. us.anthropic.claude-sonnet-4-6) - ARNs and guardrails are supported via the serverless backend</li>
                        <li>Cloud mode routes through a serverless backend (CloudFront → API Gateway → Lambda) - you authenticate via Cognito</li>
                      </>
                    ) : (
                      <>
                        <li>Select the profile type - Bedrock (InvokeModelWithResponseStream, ConverseStream, + Guardrail), Direct Anthropic API, or Grok (xAI)</li>
                        <li>Enter a Model ID or ARN - use a system inference profile ID (e.g. us.anthropic.claude-sonnet-4-6), application inference profile ARN, or direct model ID</li>
                        <li>For guardrail profiles, enter the Guardrail ID (just the ID, not the full ARN) and version (DRAFT or a version number)</li>
                        <li>Local mode uses your own AWS credentials - configure them in Settings</li>
                      </>
                    )}
                    <li>Choose a prompt complexity - Simple, Medium, or Complex. You can also set a custom prompt and max tokens</li>
                    <li>Toggle "Enabled" on the profiles you want to include in the benchmark</li>
                  </ol>
                </div>
                <div style={styles.grid}>
                  {profiles.map((p) => (
                    <ProfileCard
                      key={p.id}
                      profile={p}
                      onToggle={(id, enabled) => updateProfile(id, { enabled })}
                      onEdit={(profile) => setEditing(profile)}
                      onDelete={deleteProfile}
                    />
                  ))}
                </div>
              </SpaceBetween>
            ),
          },
        ]}
      />

      {(editing || adding) && (
        <ProfileEditor
          profile={editing ?? undefined}
          onSave={(profile) => {
            if (editing) updateProfile(profile.id, profile)
            else addProfile(profile)
            setEditing(null)
            setAdding(false)
          }}
          onCancel={() => { setEditing(null); setAdding(false) }}
        />
      )}
    </SpaceBetween>
  )
}

const styles: Record<string, React.CSSProperties> = {
  howto: { backgroundColor: colorBackgroundContainerContent, borderRadius: 8, border: `1px solid ${colorBorderDividerDefault}`, padding: 14 },
  howtoList: { color: colorTextBodySecondary, fontSize: 13, lineHeight: 1.8, paddingLeft: 18, margin: 0 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
}
